// ** React Imports
import { useState, useEffect, SyntheticEvent } from 'react'
import {
    Box,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Typography,
    Divider,
    Stack
} from '@mui/material'
import axios from 'axios'
import apiUrl from '../../../configs/constant';
import { useToast } from '../../../components/ToastProvider'

interface Props {
    id: any
}

const formatNumber = (num: any) =>
    new Intl.NumberFormat('id-ID').format(num || 0)

const formatPercent = (num: any) =>
    num == null ? '0%' : `${num}%`

const SummaryTab = ({
    id,
}: Props) => {
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token
    const { showToast, hideToast } = useToast();

    const [tab, setTab] = useState(0)
    const [listData, setListData] = useState<any[]>([])

    const bahanBakuData = listData.filter(item => item.jenis === 'Bahan Baku')
    const jasaData = listData.filter(item => item.jenis === 'Biaya')

    useEffect(() => {
        getData(tab)
    }, [])

    const getData = (mode: any) => {
        const url = apiUrl.apiUrl + '/acc/kalkulasi-wo/summary'
        axios
            .get(url, {
                params: { id: id, mode: mode },
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    setListData(data)
                } else {
                    showToast(response.data.message, 'error');
                }
            })
    }

    const handleChange = (_: any, newValue: number) => {
        getData(newValue)
        setTab(newValue)
    }

    // dummy data (nanti ganti dari API)
    const bahanBaku = [
        { name: 'A', value: 1000 },
        { name: 'B', value: 2000 },
        { name: 'C', value: 1500 },
        { name: 'D', value: 500 }
    ]

    const jasa = [
        { name: 'A', value: 800 },
        { name: 'B', value: 1200 },
        { name: 'C', value: 600 },
        { name: 'D', value: 400 }
    ]

    const renderBahanBakuSection = (data: any[]) => {
        const total = data[0]?.total_bb || 0
        const percent = data[0]?.persen_bb

        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant='h6' gutterBottom>
                        Bahan Baku
                    </Typography>

                    <Stack spacing={1}>
                        {data.map((item, i) => (
                            <Box key={i} display='flex'>
                                <Box flex={1}>
                                    <Typography>{item.item}</Typography>
                                </Box>

                                <Box width={120} textAlign='right'>
                                    <Typography>{formatNumber(item.total_item)}</Typography>
                                </Box>

                                <Box width={60} />
                            </Box>
                        ))}

                        <Divider />

                        <Box display='flex'>
                            <Box flex={1}>
                                <Typography fontWeight='bold'>Total</Typography>
                            </Box>

                            <Box width={120} textAlign='right'>
                                <Typography fontWeight='bold'>
                                    {formatNumber(total)}
                                </Typography>
                            </Box>

                            <Box width={60} textAlign='right'>
                                <Typography fontWeight='bold'>
                                    {formatPercent(percent)}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        )
    }


    const renderJasaSection = (data: any[]) => {
        const total = data[0]?.total_b || 0
        const percent = data[0]?.persen_b

        const totalPerolehan = data[0]?.total_biaya || 0
        const totalPerolehanPercent = data[0]?.persen_biaya

        const margin = data[0]?.margin || 0
        const marginPercent = data[0]?.persen_margin

        return (
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant='h6' gutterBottom>
                        Jasa
                    </Typography>

                    <Stack spacing={1}>
                        {/* List */}
                        {data.map((item, i) => (
                            <Box key={i} display='flex'>
                                <Box flex={1}>
                                    <Typography>{item.item}</Typography>
                                </Box>

                                <Box width={120} textAlign='right'>
                                    <Typography>{formatNumber(item.total_item)}</Typography>
                                </Box>

                                <Box width={60} />
                            </Box>
                        ))}

                        <Divider />

                        {/* Total Jasa */}
                        <Box display='flex'>
                            <Box flex={1}>
                                <Typography fontWeight='bold'>Total</Typography>
                            </Box>

                            <Box width={120} textAlign='right'>
                                <Typography fontWeight='bold'>
                                    {formatNumber(total)}
                                </Typography>
                            </Box>

                            <Box width={60} textAlign='right'>
                                <Typography fontWeight='bold'>
                                    {formatPercent(percent)}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider />

                        {/* Total Perolehan */}
                        <Box display='flex'>
                            <Box flex={1}>
                                <Typography>Total Perolehan</Typography>
                            </Box>

                            <Box width={120} textAlign='right'>
                                <Typography>
                                    {formatNumber(totalPerolehan)}
                                </Typography>
                            </Box>

                            <Box width={60} textAlign='right'>
                                <Typography>
                                    {formatPercent(totalPerolehanPercent)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Margin */}
                        <Box display='flex'>
                            <Box flex={1}>
                                <Typography>Margin</Typography>
                            </Box>

                            <Box width={120} textAlign='right'>
                                <Typography color={margin < 0 ? 'error.main' : 'success.main'}>
                                    {formatNumber(margin)}
                                </Typography>
                            </Box>

                            <Box width={60} textAlign='right'>
                                <Typography color={margin < 0 ? 'error.main' : 'success.main'}>
                                    {formatPercent(marginPercent)}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        )
    }


    const renderContent = () => {
        return (
            <Box mt={2}>
                {/* Top Summary */}
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack spacing={1}>
                            <Box display='flex' justifyContent='space-between'>
                                <Typography>Qty Produksi</Typography>
                                <Typography>{formatNumber(listData[0]?.qty)}</Typography>
                            </Box>

                            <Box display='flex' justifyContent='space-between'>
                                <Typography>Harga</Typography>
                                <Typography>{formatNumber(listData[0]?.harga)}</Typography>
                            </Box>

                            <Divider />
                            <Box display='flex' justifyContent='space-between'>
                                <Typography fontWeight='bold'>Total</Typography>
                                <Typography fontWeight='bold'>{formatNumber(listData[0]?.total_jual)}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>


                {renderBahanBakuSection(bahanBakuData)}
                {renderJasaSection(jasaData)}


            </Box>
        )
    }

    return (
        <Box>
            {/* Inner Tabs */}
            <Tabs value={tab} onChange={handleChange}>
                <Tab label='ACTUAL' />
                <Tab label='BOM' />
            </Tabs>

            {/* Tab Content */}
            {tab === 0 && renderContent()}
            {tab === 1 && renderContent()}
        </Box>
    )
}

export default SummaryTab
